import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Flex, Text, Switch, Button, useToast, VStack, HStack, Badge, Divider, SimpleGrid,
    Alert, AlertIcon, Select, NumberInput, NumberInputField, FormControl, FormLabel, FormHelperText,
    Textarea, Spinner, Stack, IconButton,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FaSyncAlt, FaCheck, FaBan, FaUserShield } from 'react-icons/fa';
import {
    getBotLimiteCotacao, updateBotLimiteCotacaoConfig, aprovarBloqueioCotacao,
    manterBloqueioCotacao, isentarContatoCotacao, bloquearContatoCotacao,
} from '../services/api';

// Atraso da resposta durante o bloqueio. Até 12h: a resposta precisa sair dentro da
// janela de 24h do WhatsApp. 0 = não responde (só o aviso "bloqueado até").
const ATRASOS = [1, 2, 3, 4, 6, 8, 12];

const STATUS = {
    bloqueado: { cor: 'red', label: 'Bloqueado' },
    reprovado: { cor: 'red', label: 'Bloqueio mantido' },
    aprovado: { cor: 'green', label: 'Aprovado' },
    isento: { cor: 'green', label: 'Isento' },
    expirado: { cor: 'gray', label: 'Bloqueio venceu' },
    comprou: { cor: 'blue', label: 'Comprou' },
};

const SITUACAO = {
    normal: { cor: 'gray', label: 'normal' },
    aviso: { cor: 'yellow', label: 'recebendo aviso' },
    no_limite: { cor: 'orange', label: 'no limite' },
    bloqueado: { cor: 'red', label: 'bloqueado' },
    isento: { cor: 'green', label: 'isento' },
};

const MOTIVO = { cotacoes: 'peças cotadas', custo: 'gasto de IA', manual: 'bloqueio na tela', isencao: 'isenção na tela' };

const dolar = (v) => `US$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatarData = (s) => {
    if (!s) return '-';
    const d = new Date(String(s).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatarTelefone = (t) => {
    const d = String(t || '').replace(/\D/g, '');
    const n = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
    if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
    if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
    return t || 'sem telefone';
};

// Mesmas variáveis do backend: {restantes}, {data}, {horas}, {dias}.
const renderizar = (texto, vars) => Object.entries(vars)
    .reduce((t, [k, v]) => t.split(`{${k}}`).join(String(v)), texto || '');

// Quanto ainda cabe, para quem segue contando (bloqueado e isento não contam agora).
// Sem limite de cotações, só o gasto conta: mostra quanto falta para ele.
const restantesDaPrevia = (p, config) => {
    if (p.situacao === 'bloqueado') return 'já bloqueado';
    if (p.situacao === 'isento') {
        return p.ultima_compra ? `isento: comprou em ${formatarData(p.ultima_compra)}` : 'isento da contagem';
    }
    if (p.restantes === null || p.restantes === undefined) {
        const limite = Number(config.limite_custo_usd) || 0;
        return limite > 0 ? `faltam ${dolar(Math.max(0, limite - Number(p.custo_usd || 0)))} de IA` : 'sem limite configurado';
    }
    if (p.restantes <= 0) return 'chegou ao limite: os próximos avisos são os últimos';
    return `cabem mais ${p.restantes} peça${p.restantes === 1 ? '' : 's'}`;
};

const Estatistica = ({ label, valor, cor }) => (
    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
        <Text fontSize="xs" color="gray.500">{label}</Text>
        <Text fontSize="xl" fontWeight="bold" color={cor}>{valor}</Text>
    </Box>
);

// Guarda o texto digitado (apagar o campo não vira 0 no meio da digitação); o
// número sai no salvar. Aceita vírgula: é o que o teclado do celular em pt-BR mostra.
const Numero = ({ label, ajuda, valor, onChange, min = 0, max, passo = 1, precisao = 0 }) => (
    <FormControl>
        <FormLabel fontSize="sm" mb={1}>{label}</FormLabel>
        <NumberInput
            size="sm" min={min} max={max} step={passo} precision={precisao}
            value={valor ?? ''} onChange={(texto) => onChange(texto)}
            isValidCharacter={(c) => /^[0-9.,]$/.test(c)}
            parse={(texto) => texto.replace(',', '.')}
        >
            <NumberInputField inputMode={precisao > 0 ? 'decimal' : 'numeric'} />
        </NumberInput>
        {ajuda && <FormHelperText fontSize="xs">{ajuda}</FormHelperText>}
    </FormControl>
);

const CampoTexto = ({ label, ajuda, valor, padrao, exemplo, aviso, onChange }) => (
    <FormControl>
        <Flex justify="space-between" align="baseline" gap={2} wrap="wrap">
            <FormLabel fontSize="sm" mb={1}>{label}</FormLabel>
            {valor !== padrao && (
                <Button size="xs" variant="link" colorScheme="orange" onClick={() => onChange(padrao)}>
                    Voltar ao padrão
                </Button>
            )}
        </Flex>
        <Textarea size="sm" rows={3} value={valor} onChange={(e) => onChange(e.target.value)} />
        <FormHelperText fontSize="xs">{ajuda}</FormHelperText>
        <Text fontSize="xs" color="gray.600" mt={1} whiteSpace="pre-wrap" wordBreak="break-word">
            <b>Fica assim:</b> {exemplo || '—'}
        </Text>
        {aviso && <Text fontSize="xs" color="orange.600" mt={1}>{aviso}</Text>}
    </FormControl>
);

const CartaoBloqueio = ({ b, decidindo, onDesbloquear, onManter }) => {
    const st = STATUS[b.status] || { cor: 'gray', label: b.status };
    const restrito = b.status === 'bloqueado' || b.status === 'reprovado';
    const manual = b.motivo === 'manual';
    const isencao = b.status === 'isento' || b.motivo === 'isencao';
    return (
        <Box borderRadius="md" border="1px solid" borderColor={b.aguardando_analise ? 'red.200' : 'gray.200'} p={{ base: 3, md: 4 }}>
            <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
                <Box minW={0}>
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{b.nome || 'Sem nome'}</Text>
                    <Text fontSize="xs" color="gray.500">{formatarTelefone(b.telefone)}</Text>
                </Box>
                <VStack spacing={1} align="flex-end" flexShrink={0}>
                    <Badge colorScheme={st.cor} whiteSpace="normal" textAlign="center">{st.label}</Badge>
                    {b.aguardando_analise && <Badge colorScheme="orange">aguardando análise</Badge>}
                </VStack>
            </Flex>

            {manual && (
                <Text fontSize="xs" color="gray.600">
                    Bloqueado na tela{b.decidido_por ? ` por ${b.decidido_por}` : ''}: {b.cotacoes} peças cotadas e {dolar(b.custo_usd)} de IA nos 30 dias antes.
                </Text>
            )}
            {isencao && <Text fontSize="xs" color="gray.600">Isentado na tela.</Text>}
            {!manual && !isencao && (
                <Text fontSize="xs" color="gray.600">
                    Bateu o limite por <b>{MOTIVO[b.motivo] || b.motivo}</b>: {b.cotacoes} peças cotadas · {dolar(b.custo_usd)} de IA
                </Text>
            )}
            {b.bloqueado_em && (
                <Text fontSize="xs" color="gray.600">
                    Bloqueado em {formatarData(b.bloqueado_em)}
                    {restrito && <> até <b>{formatarData(b.bloqueado_ate)}</b></>}
                    {b.status === 'aprovado' && ` · liberado em ${formatarData(b.decidido_em)}`}
                    {b.status === 'comprou' && ` · comprou em ${formatarData(b.encerrado_em)}`}
                    {b.status === 'expirado' && ` · venceu em ${formatarData(b.bloqueado_ate)}`}
                </Text>
            )}
            {b.isento_ate && (b.status === 'aprovado' || b.status === 'isento') && (
                <Text fontSize="xs" color="green.700">Normal até {formatarData(b.isento_ate)} (isento do bloqueio automático)</Text>
            )}
            {b.resposta_pendente && (
                <Text fontSize="xs" color="purple.600">
                    {b.responder_apos
                        ? `Resposta do bot segurada: sai às ${formatarData(b.responder_apos)}`
                        : 'Mensagem do cliente sem resposta do bot (bloqueio em "não responder")'}
                </Text>
            )}

            {b.mensagem_limite && (
                <Box mt={2} pl={2} borderLeft="3px solid" borderColor="orange.200">
                    <Text fontSize="xs" color="gray.500">
                        Pergunta enviada{!b.mensagem_limite_enviada && ' (não saiu — erro na Poli)'}:
                    </Text>
                    <Text fontSize="xs" fontStyle="italic" color="gray.700" whiteSpace="pre-wrap" wordBreak="break-word">
                        {b.mensagem_limite}
                    </Text>
                </Box>
            )}

            {!isencao && (
                <Box mt={2}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                        {manual ? 'O que o cliente mandou no bloqueio' : 'Resposta do cliente'}
                    </Text>
                    {b.respostas?.length > 0 ? (
                        <VStack align="stretch" spacing={1} mt={1}>
                            {b.respostas.map((r) => (
                                <Box key={r.id} bg="gray.50" borderRadius="md" px={2} py={1}>
                                    <Text fontSize="xs" color="gray.500">{formatarData(r.em)}</Text>
                                    <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">{r.texto || '(vazio)'}</Text>
                                </Box>
                            ))}
                        </VStack>
                    ) : (
                        <Text fontSize="xs" color="gray.400">{manual ? 'Ainda não escreveu.' : 'Ainda não respondeu.'}</Text>
                    )}
                </Box>
            )}

            {b.decidido_em && !manual && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                    Decidido por {b.decidido_por || '—'} em {formatarData(b.decidido_em)}
                    {b.observacao ? ` — ${b.observacao}` : ''}
                </Text>
            )}
            {manual && b.observacao && (
                <Text fontSize="xs" color="gray.500" mt={2}>Obs.: {b.observacao}</Text>
            )}

            {restrito && (
                <Stack direction={{ base: 'column', sm: 'row' }} mt={3} spacing={2}>
                    <Button
                        size="sm" colorScheme="green" leftIcon={<FaCheck />}
                        isLoading={decidindo === b.id} onClick={() => onDesbloquear(b)}
                    >
                        Desbloquear
                    </Button>
                    {b.aguardando_analise && (
                        <Button
                            size="sm" colorScheme="red" variant="outline" leftIcon={<FaBan />}
                            isLoading={decidindo === b.id} onClick={() => onManter(b)}
                        >
                            Manter bloqueio
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
};

// Uma linha do resumo: o número em destaque é o da lista (gasto ou peças).
const LinhaRanking = ({ p, posicao, destaque, decidindo, onBloquear }) => {
    const gasto = `${dolar(p.custo_usd)} de IA`;
    const pecas = `${p.cotacoes} peça${p.cotacoes === 1 ? '' : 's'}`;
    return (
        <Flex align="center" justify="space-between" gap={2} py={2} borderBottom="1px solid" borderColor="gray.100">
            <Box minW={0}>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{posicao}. {p.nome || 'Sem nome'}</Text>
                <Text fontSize="xs" color="gray.700">
                    <b>{destaque === 'gasto' ? gasto : pecas}</b> · {destaque === 'gasto' ? pecas : gasto}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {p.sem_telefone ? 'sem telefone' : formatarTelefone(p.telefone)} · último contato {formatarData(p.ultimo_uso)}
                </Text>
            </Box>
            {p.bloqueado_ate && (
                <Badge colorScheme="red" flexShrink={0} whiteSpace="normal" textAlign="center">
                    bloqueado até {formatarData(p.bloqueado_ate).slice(0, 5)}
                </Badge>
            )}
            {!p.bloqueado_ate && (
                <Button
                    size="xs" colorScheme="red" variant="outline" leftIcon={<FaBan />} flexShrink={0}
                    isLoading={decidindo === `rk-${p.poli_contact_id}`} onClick={() => onBloquear(p)}
                >
                    Bloquear
                </Button>
            )}
        </Flex>
    );
};

const BotLimiteCotacaoTab = () => {
    const toast = useToast();
    const [config, setConfig] = useState(null);
    const [padroes, setPadroes] = useState({});
    const [bloqueios, setBloqueios] = useState([]);
    const [previa, setPrevia] = useState([]);
    const [ranking, setRanking] = useState(null);
    const [resumo, setResumo] = useState({});
    const [dias, setDias] = useState(30);
    const [erro, setErro] = useState(false);
    const [atualizando, setAtualizando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [decidindo, setDecidindo] = useState(null);
    const decisaoModal = useDisclosure();
    const [decisao, setDecisao] = useState(null); // { tipo: bloquear|aprovar|manter|isentar, alvo, dias, observacao }

    // Só a primeira carga traz a config: a atualização das listas não pode
    // sobrescrever o que está sendo editado nos campos.
    const jaCarregou = useRef(false);
    const carregar = useCallback(async () => {
        setAtualizando(true);
        try {
            const { data } = await getBotLimiteCotacao({ dias });
            if (!jaCarregou.current) {
                setConfig(data.config);
                setPadroes(data.textos_padrao || {});
                jaCarregou.current = true;
            }
            setBloqueios(data.bloqueios || []);
            setPrevia(data.previa || []);
            setRanking(data.ranking || null);
            setResumo(data.resumo || {});
            setErro(false);
        } catch (e) {
            setErro(true);
            toast({ title: 'Erro ao carregar as cotações sem compra.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setAtualizando(false);
        }
    }, [toast, dias]);

    useEffect(() => {
        carregar();
        const t = setInterval(carregar, 60000);
        return () => clearInterval(t);
    }, [carregar]);

    if (!config) {
        return erro ? (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                Não deu para carregar as cotações sem compra.
                <Button size="sm" ml={3} onClick={carregar}>Tentar de novo</Button>
            </Alert>
        ) : (
            <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>
        );
    }

    const campo = (chave, valor) => setConfig((c) => ({ ...c, [chave]: valor }));
    const padraoBloqueio = Number(config.atraso_horas) > 0 ? padroes.mensagem_bloqueio : padroes.mensagem_bloqueio_sem_resposta;
    const textoAviso = config.mensagem_aviso ?? padroes.mensagem_aviso ?? '';
    const textoLimite = config.mensagem_limite ?? padroes.mensagem_limite ?? '';
    const textoBloqueio = config.mensagem_bloqueio ?? padraoBloqueio ?? '';
    const fimBloqueio = new Date(Date.now() + Number(config.bloqueio_dias || 0) * 86400000)
        .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const vars = {
        restantes: Math.max(1, Number(config.aviso_restantes) || 3),
        data: fimBloqueio,
        horas: Number(config.atraso_horas) > 0 ? config.atraso_horas : '',
        dias: config.bloqueio_dias,
    };

    const salvar = async (payload, mensagem) => {
        setSalvando(true);
        try {
            const { data } = await updateBotLimiteCotacaoConfig(payload);
            setConfig(data);
            if (mensagem) toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
            carregar();
        } catch (e) {
            toast({
                title: e?.response?.data?.message || 'Erro ao salvar a configuração.',
                status: 'error', duration: 5000, isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    // Texto igual ao padrão vai como null: continua acompanhando o padrão (o do
    // bloqueio muda com o atraso).
    const salvarTudo = () => salvar({
        limite_cotacoes: Number(config.limite_cotacoes) || 0,
        limite_custo_usd: Number(config.limite_custo_usd) || 0,
        janela_dias: Number(config.janela_dias) || 30,
        aviso_restantes: Number(config.aviso_restantes) || 0,
        bloqueio_dias: Number(config.bloqueio_dias) || 7,
        atraso_horas: Number(config.atraso_horas) || 0,
        isencao_dias: Number(config.isencao_dias) || 30,
        mensagem_aviso: textoAviso === padroes.mensagem_aviso ? null : textoAviso,
        mensagem_limite: textoLimite === padroes.mensagem_limite ? null : textoLimite,
        mensagem_bloqueio: textoBloqueio === padraoBloqueio ? null : textoBloqueio,
    }, 'Configuração salva.');

    // Bloqueio manual nasce com 30 dias; desbloquear/isentar usam a isenção da config.
    const abrirDecisao = (tipo, alvo) => {
        setDecisao({ tipo, alvo, dias: tipo === 'bloquear' ? 30 : config.isencao_dias, observacao: '' });
        decisaoModal.onOpen();
    };

    const confirmarDecisao = async () => {
        if (!decisao) return;
        const { tipo, alvo } = decisao;
        const chave = tipo === 'bloquear' ? `rk-${alvo.poli_contact_id}` : (alvo.id ?? `previa-${alvo.poli_contact_id}`);
        setDecidindo(chave);
        try {
            const observacao = decisao.observacao?.trim() || null;
            if (tipo === 'bloquear') {
                await bloquearContatoCotacao({
                    poli_contact_id: alvo.poli_contact_id, nome: alvo.nome, telefone: alvo.telefone,
                    dias: Number(decisao.dias) || null, observacao,
                });
            } else if (tipo === 'aprovar') {
                await aprovarBloqueioCotacao(alvo.id, { dias: Number(decisao.dias) || null, observacao });
            } else if (tipo === 'manter') {
                await manterBloqueioCotacao(alvo.id, { observacao });
            } else {
                await isentarContatoCotacao({
                    poli_contact_id: alvo.poli_contact_id, nome: alvo.nome, telefone: alvo.telefone,
                    dias: Number(decisao.dias) || null, observacao,
                });
            }
            const titulos = { bloquear: 'Contato bloqueado.', aprovar: 'Contato desbloqueado.', manter: 'Bloqueio mantido.', isentar: 'Contato isentado.' };
            toast({ title: titulos[tipo], status: 'success', duration: 3000, isClosable: true });
            decisaoModal.onClose();
            carregar();
        } catch (e) {
            toast({ title: e?.response?.data?.message || 'Não deu para salvar.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setDecidindo(null);
        }
    };

    const restritos = bloqueios.filter((b) => b.status === 'bloqueado' || b.status === 'reprovado');
    const historico = bloqueios.filter((b) => b.status !== 'bloqueado' && b.status !== 'reprovado');
    const semLimite = !(Number(config.limite_cotacoes) > 0) && !(Number(config.limite_custo_usd) > 0);
    const tituloModal = {
        bloquear: 'Bloquear contato',
        aprovar: 'Desbloquear contato',
        manter: 'Manter o bloqueio',
        isentar: 'Isentar do bloqueio automático',
    };

    return (
        <VStack spacing={5} align="stretch">

            {/* Resumo + bloqueio automático */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg">Cotações sem compra</Text>
                <Text fontSize="sm" color="gray.500">
                    Bloqueie um por um quem usa o bot só para cotar. O bloqueado recebe o aviso "até DD/MM" quando
                    escreve e o bot só responde depois do atraso; pergunta de pós-venda (pedido, entrega, nota)
                    segue normal. Compra no site ou a data encerram o bloqueio, e "Desbloquear" libera na hora.
                </Text>

                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={4}>
                    <Estatistica label="Bloqueados agora" valor={resumo.restritos ?? 0} cor="red.500" />
                    <Estatistica label="Respostas seguradas" valor={resumo.respostas_pendentes ?? 0} cor="purple.500" />
                    <Estatistica label={`Sem pedido (${ranking?.dias ?? dias} dias)`} valor={ranking?.contatos_sem_pedido ?? 0} />
                    <Estatistica label="IA gasta com eles" valor={dolar(ranking?.gasto_sem_pedido_usd)} cor="orange.500" />
                </SimpleGrid>

                <Divider my={4} />

                <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                    <Box flex="1" minW="220px">
                        <Text fontWeight="semibold" fontSize="sm">Bloqueio automático</Text>
                        <Text fontSize="xs" color="gray.500">
                            {config.ativo
                                ? 'Ligado: quem passa do limite recebe o aviso 3-2-1 e é bloqueado sozinho.'
                                : 'Desligado: ninguém é avisado nem bloqueado sozinho — só quem você bloquear aqui.'}
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Badge colorScheme={config.ativo ? 'green' : 'gray'} fontSize="0.8em" px={2} py={1} borderRadius="md">
                            {config.ativo ? 'LIGADO' : 'DESLIGADO'}
                        </Badge>
                        <Switch
                            size="lg" colorScheme="orange" aria-label="Ligar ou desligar o bloqueio automático"
                            isChecked={!!config.ativo} isDisabled={salvando}
                            onChange={(e) => salvar(
                                { ativo: e.target.checked },
                                e.target.checked ? 'Bloqueio automático ligado.' : 'Bloqueio automático desligado.'
                            )}
                        />
                    </HStack>
                </Flex>
            </Box>

            {/* Quem mais gasta e cota sem nunca ter feito pedido */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" gap={3} mb={1} wrap="wrap">
                    <Text fontWeight="bold" fontSize="lg">Quem mais gasta e cota sem nunca ter feito pedido</Text>
                    <HStack>
                        <Select size="sm" w="130px" value={dias} onChange={(e) => setDias(Number(e.target.value))} aria-label="Período">
                            <option value={30}>30 dias</option>
                            <option value={60}>60 dias</option>
                            <option value={90}>90 dias</option>
                        </Select>
                        <IconButton
                            aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost"
                            isLoading={atualizando} onClick={carregar}
                        />
                    </HStack>
                </Flex>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    Só entra quem nunca fez pedido pago com o telefone da conversa, em nenhum canal (compra no Mercado
                    Livre não aparece: o pedido chega sem telefone). Peças = peças diferentes que o bot cotou com
                    link, no máximo 3 por resposta. Gasto = toda a IA usada com o contato.
                </Text>
                {!ranking || ranking.contatos_sem_pedido === 0 ? (
                    <Text fontSize="sm" color="gray.400">Ninguém sem pedido no período.</Text>
                ) : (
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 5, lg: 8 }}>
                        <Box>
                            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={1}>Mais gastam IA</Text>
                            {ranking.gasto.map((p, i) => (
                                <LinhaRanking
                                    key={`g-${p.poli_contact_id}`} p={p} posicao={i + 1} destaque="gasto"
                                    decidindo={decidindo} onBloquear={(x) => abrirDecisao('bloquear', x)}
                                />
                            ))}
                        </Box>
                        <Box>
                            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={1}>Mais cotam peças</Text>
                            {ranking.pecas.map((p, i) => (
                                <LinhaRanking
                                    key={`p-${p.poli_contact_id}`} p={p} posicao={i + 1} destaque="pecas"
                                    decidindo={decidindo} onBloquear={(x) => abrirDecisao('bloquear', x)}
                                />
                            ))}
                        </Box>
                    </SimpleGrid>
                )}
            </Box>

            {/* Bloqueados agora */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Bloqueados agora ({restritos.length})</Text>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    Desbloquear volta o contato ao normal na hora e responde o que ele mandou no bloqueio.
                </Text>
                {restritos.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">Ninguém bloqueado.</Text>
                ) : (
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
                        {restritos.map((b) => (
                            <CartaoBloqueio
                                key={b.id} b={b} decidindo={decidindo}
                                onDesbloquear={(x) => abrirDecisao('aprovar', x)}
                                onManter={(x) => abrirDecisao('manter', x)}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Box>

            {/* Prévia do automático (só ligado) */}
            {config.ativo && (
                <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>Perto do limite (bloqueio automático)</Text>
                    <Text fontSize="sm" color="gray.500" mb={4}>
                        Quem mais cotou sem comprar nos últimos {config.janela_dias} dias (desde a última compra e a
                        última liberação). Quem comprou nos últimos 90 dias fica isento.
                    </Text>
                    {previa.length === 0 ? (
                        <Text fontSize="sm" color="gray.400">Ninguém com cotação na janela.</Text>
                    ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                            {previa.map((p) => {
                                const s = SITUACAO[p.situacao] || SITUACAO.normal;
                                const podeIsentar = p.situacao !== 'isento' && p.situacao !== 'bloqueado';
                                return (
                                    <Box key={p.poli_contact_id} borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                                        <Flex justify="space-between" align="flex-start" gap={2}>
                                            <Box minW={0}>
                                                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{p.nome || 'Sem nome'}</Text>
                                                <Text fontSize="xs" color="gray.500">{formatarTelefone(p.telefone)}</Text>
                                            </Box>
                                            <Badge colorScheme={s.cor} flexShrink={0}>{s.label}</Badge>
                                        </Flex>
                                        <HStack spacing={4} mt={2} fontSize="xs" color="gray.700" wrap="wrap">
                                            <Text>
                                                <b>{p.cotacoes}</b>
                                                {Number(config.limite_cotacoes) > 0 ? ` de ${config.limite_cotacoes}` : ''} peças
                                            </Text>
                                            <Text>
                                                <b>{dolar(p.custo_usd)}</b>
                                                {Number(config.limite_custo_usd) > 0 ? ` de ${dolar(config.limite_custo_usd)}` : ''} de IA
                                            </Text>
                                        </HStack>
                                        <Flex justify="space-between" align="center" mt={2} gap={2} wrap="wrap">
                                            <Text fontSize="xs" color="gray.500">
                                                {restantesDaPrevia(p, config)}
                                                {p.ultimo_uso ? ` · último contato ${formatarData(p.ultimo_uso)}` : ''}
                                            </Text>
                                            {podeIsentar && (
                                                <Button
                                                    size="xs" variant="outline" colorScheme="green" leftIcon={<FaUserShield />}
                                                    isLoading={decidindo === `previa-${p.poli_contact_id}`}
                                                    onClick={() => abrirDecisao('isentar', p)}
                                                >
                                                    Isentar
                                                </Button>
                                            )}
                                        </Flex>
                                    </Box>
                                );
                            })}
                        </SimpleGrid>
                    )}
                </Box>
            )}

            {/* Configuração */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Configuração</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                    O atraso da resposta e o aviso de bloqueio valem para todo bloqueio, manual ou automático. Os
                    limites e o aviso prévio só servem para o bloqueio automático.
                </Text>

                <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Durante o bloqueio</Text>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
                    <FormControl>
                        <FormLabel fontSize="sm" mb={1}>O bot responde</FormLabel>
                        <Select
                            size="sm" value={String(config.atraso_horas)}
                            onChange={(e) => campo('atraso_horas', Number(e.target.value))}
                        >
                            {ATRASOS.map((h) => (
                                <option key={h} value={h}>{`depois de ${h}h`}</option>
                            ))}
                            <option value={0}>não responde (só o aviso)</option>
                        </Select>
                        <FormHelperText fontSize="xs">A resposta sai pelo bot, como sempre, só que atrasada.</FormHelperText>
                    </FormControl>
                    <Numero
                        label="Bloqueio automático dura (dias)" ajuda="No manual você escolhe ao bloquear (máx. 60 aqui)."
                        valor={config.bloqueio_dias} min={1} max={60} onChange={(v) => campo('bloqueio_dias', v)}
                    />
                    <Numero
                        label="Ao desbloquear, fica isento por (dias)" ajuda="Sem aviso nem bloqueio automático nesse período."
                        valor={config.isencao_dias} min={1} max={365} onChange={(v) => campo('isencao_dias', v)}
                    />
                </SimpleGrid>

                <Divider my={5} />

                <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Bloqueio automático (limites)</Text>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
                    <Numero
                        label="Limite de peças cotadas sem compra" ajuda="Peças diferentes que o bot cotou com link. 0 = sem limite."
                        valor={config.limite_cotacoes} max={500} onChange={(v) => campo('limite_cotacoes', v)}
                    />
                    <Numero
                        label="Limite de gasto de IA (US$)" ajuda="Use alto (ex.: 20): quem gasta muito de IA costuma comprar. 0 = sem limite."
                        valor={config.limite_custo_usd} max={100} passo={0.5} precisao={2}
                        onChange={(v) => campo('limite_custo_usd', v)}
                    />
                    <Numero
                        label="Janela (dias)" ajuda="Só conta o que ele fez nestes últimos dias (máx. 90)."
                        valor={config.janela_dias} min={1} max={90} onChange={(v) => campo('janela_dias', v)}
                    />
                    <Numero
                        label="Aviso prévio: avisar quando faltarem" ajuda="Peças novas antes do bloqueio. 0 = bloqueia sem aviso."
                        valor={config.aviso_restantes} max={10} onChange={(v) => campo('aviso_restantes', v)}
                    />
                </SimpleGrid>
                {semLimite && (
                    <Text fontSize="xs" color="orange.600" mt={2}>
                        Os dois limites estão em zero: o bloqueio automático não pega ninguém (e não liga assim).
                    </Text>
                )}

                <Divider my={5} />

                <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Mensagens</Text>
                <VStack spacing={5} align="stretch">
                    <CampoTexto
                        label="Aviso de bloqueio (quando o bloqueado escreve; no máximo 1 a cada 6h)"
                        ajuda="Use {data} para o fim do bloqueio e {horas} para o atraso da resposta."
                        valor={textoBloqueio} padrao={padraoBloqueio}
                        exemplo={renderizar(textoBloqueio, vars)}
                        aviso={Number(config.atraso_horas) === 0 && textoBloqueio.includes('{horas}')
                            ? 'O bloqueio está em "não responde", mas o texto promete resposta em {horas}h.' : null}
                        onChange={(v) => campo('mensagem_bloqueio', v)}
                    />
                    <CampoTexto
                        label="Aviso prévio (só no bloqueio automático)"
                        ajuda="Use {restantes} para quantas vezes ainda dá para cotar peça nova."
                        valor={textoAviso} padrao={padroes.mensagem_aviso}
                        exemplo={renderizar(textoAviso, vars)}
                        aviso={!textoAviso.includes('{restantes}') ? 'O texto não tem {restantes}: o cliente não vai ver quantas faltam.' : null}
                        onChange={(v) => campo('mensagem_aviso', v)}
                    />
                    <CampoTexto
                        label="Pergunta do limite (só no bloqueio automático)"
                        ajuda="Use {data} para o fim do bloqueio e {dias} para a duração. A resposta do cliente aparece aqui."
                        valor={textoLimite} padrao={padroes.mensagem_limite}
                        exemplo={renderizar(textoLimite, vars)}
                        onChange={(v) => campo('mensagem_limite', v)}
                    />
                </VStack>

                <Flex justify="flex-end" mt={5}>
                    <Button colorScheme="orange" onClick={salvarTudo} isLoading={salvando} w={{ base: 'full', sm: 'auto' }}>
                        Salvar configuração
                    </Button>
                </Flex>
            </Box>

            {/* Histórico */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Histórico (60 dias)</Text>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    Bloqueios já encerrados (desbloqueado, comprou, venceu) e as isenções.
                </Text>
                {historico.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">Nada por aqui ainda.</Text>
                ) : (
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
                        {historico.map((b) => (
                            <CartaoBloqueio
                                key={b.id} b={b} decidindo={decidindo}
                                onDesbloquear={(x) => abrirDecisao('aprovar', x)}
                                onManter={(x) => abrirDecisao('manter', x)}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Box>

            {/* Bloquear, desbloquear, manter ou isentar */}
            <Modal isOpen={decisaoModal.isOpen} onClose={decisaoModal.onClose} size={{ base: 'full', md: 'md' }}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">{decisao ? tituloModal[decisao.tipo] : ''}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {decisao && (
                            <VStack align="stretch" spacing={4}>
                                <Text fontSize="sm">
                                    <b>{decisao.alvo.nome || 'Sem nome'}</b> · {formatarTelefone(decisao.alvo.telefone)}
                                </Text>
                                {decisao.tipo === 'bloquear' && (
                                    <>
                                        <Text fontSize="sm" color="gray.600">
                                            Nada é enviado agora. Quando ele escrever, recebe o aviso de bloqueio e o
                                            bot só responde {Number(config.atraso_horas) > 0 ? `depois de ${config.atraso_horas}h` : 'quando você desbloquear'}.
                                        </Text>
                                        <Numero
                                            label="Bloqueado por (dias)" ajuda="Compra no site encerra antes. Dá para desbloquear quando quiser."
                                            valor={decisao.dias} min={1} max={3650}
                                            onChange={(v) => setDecisao((d) => ({ ...d, dias: v }))}
                                        />
                                    </>
                                )}
                                {decisao.tipo === 'manter' && (
                                    <Text fontSize="sm" color="gray.600">
                                        Continua bloqueado até {formatarData(decisao.alvo.bloqueado_ate)}. Dá para
                                        desbloquear depois, se mudar de ideia.
                                    </Text>
                                )}
                                {(decisao.tipo === 'aprovar' || decisao.tipo === 'isentar') && (
                                    <Numero
                                        label="Fica isento do bloqueio automático por (dias)"
                                        ajuda="Nesse período ele não recebe aviso nem bloqueio automático. Bloqueio manual continua possível."
                                        valor={decisao.dias} min={1} max={3650}
                                        onChange={(v) => setDecisao((d) => ({ ...d, dias: v }))}
                                    />
                                )}
                                <FormControl>
                                    <FormLabel fontSize="sm" mb={1}>Observação (opcional)</FormLabel>
                                    <Textarea
                                        size="sm" rows={3} value={decisao.observacao}
                                        placeholder={decisao.tipo === 'bloquear' ? 'Ex.: revenda, só cota para cliente' : 'Ex.: mecânico, compra pelo balcão'}
                                        onChange={(e) => setDecisao((d) => ({ ...d, observacao: e.target.value }))}
                                    />
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={3} onClick={decisaoModal.onClose}>Cancelar</Button>
                        <Button
                            size="sm" colorScheme={decisao?.tipo === 'bloquear' || decisao?.tipo === 'manter' ? 'red' : 'green'}
                            isLoading={decidindo !== null} onClick={confirmarDecisao}
                        >
                            {decisao?.tipo === 'bloquear' ? 'Bloquear' : decisao?.tipo === 'manter' ? 'Manter bloqueio' : 'Confirmar'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

export default BotLimiteCotacaoTab;
